import { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  Grid,
  Text,
  Divider,
  ReactSelectController,
  RadioController,
  Toast,
  Icons,
  CheckboxController,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { defaultToastOptions, sleep } from "../../../utils/misc";
import { useToggle } from "../../../hooks/state";
import {
  getWaitlistExpirationOffset,
  waitlistExpireHours,
  waitlistExpireMinutes,
} from "../Calendar.utils";
import { useForm, useWatch } from "react-hook-form";
import SubscriptionSelect from "./../../../components/controllers/SubscriptionSelect";
import MemberProfileSelect from "./../../../components/controllers/MemberProfileSelect";
import { SubscriptionOptionFragment } from "../../../graphql/graphql-generated.types";
import { MemberProfileFragment } from "../../../components/controllers/MemberProfileSelect/MemberProfileSelect-generated.hooks";
import { useGetCalendarItemDetailQuery } from "../CalendarDetail/CalendarDetail-generated.hooks";
import { AppContext } from "../../../context/app";
import {
  useCreateReservationMutation,
  useGetDocumentsQuery,
  useJoinWaitlistMutation,
} from "./AddMember-generated.hooks";
import { source } from "./../CalendarDetail/CalendarDetail.utils";
import styles from "./AddMemberModal.module.scss";
import { useCheckinMutation } from "../../../apis/calendarApi.hooks";
import { Dialog, DialogPortal, DialogContent } from "@pushpress/pushpress-ui";

type AddMemberModalProps = {
  calendarUuid: string;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  visible: boolean;
  noPortal?: boolean;
  useDialog?: boolean;
};

type ReservationType = "waitlist" | "reserve" | "checkin";

const AddMemberModal = (props: AddMemberModalProps) => {
  return props?.useDialog ? (
    <Dialog open={props.visible} onOpenChange={props.onClose}>
      <AddMemberContent {...props} />
    </Dialog>
  ) : (
    <Modal
      visible={props.visible}
      closeModal={props.onClose}
      verticalAlign="top"
      disableClickAway
      disableEscapeKeyDown
    >
      <AddMemberContent {...props} />
    </Modal>
  );
};

interface Form {
  profile?: MemberProfileFragment;
  staffCheckIn?: boolean;
  coachType?: "coach" | "assistant";
  reservationType: ReservationType;
  subscription?: SubscriptionOptionFragment;
  waitlistExpireHours?: {
    label: string;
    value: string | number;
  };
  waitlistExpireMinutes?: {
    label: string;
    value: string | number;
  };
}

const AddMemberContent = ({
  onClose,
  onSuccess,
  calendarUuid,
  noPortal,
  useDialog,
}: AddMemberModalProps) => {
  const { control, handleSubmit, setValue } = useForm<Form>({
    mode: "onTouched",
    defaultValues: {
      waitlistExpireHours: waitlistExpireHours?.[0],
      waitlistExpireMinutes: waitlistExpireMinutes?.[2],
    },
  });
  const { client } = useContext(AppContext);
  const { profile, reservationType, subscription, staffCheckIn } = useWatch({
    control,
  });

  const { data } = useGetCalendarItemDetailQuery({
    variables: {
      getCalendarItemInput: {
        uuid: calendarUuid,
        clientUuid: client.uuid,
      },
    },
  });
  const calendarItem = data?.getCalendarItem;
  const [addClientLoading, addClientLoadingActions] = useToggle(false);
  const [createReservation] = useCreateReservationMutation();
  const checkInMutation = useCheckinMutation();
  const [joinWaitlist] = useJoinWaitlistMutation();
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    SubscriptionOptionFragment[] | undefined
  >(undefined);
  const { data: documentsData } = useGetDocumentsQuery({
    variables: {
      getDocumentsInput: {
        userUuid: profile?.userUuid,
      },
    },
    skip: !profile?.userUuid,
  });
  const waitListAvailable = useMemo(() => {
    if (!calendarItem) return;

    let waitListAvailable = false;

    if (calendarItem.attendanceCap > 0 && calendarItem.spotsAvailable < 1) {
      waitListAvailable = true;
    }

    return waitListAvailable;
  }, [calendarItem]);

  useEffect(() => {
    if (subscriptionOptions && subscriptionOptions?.length > 0) {
      setValue("subscription", subscriptionOptions?.[0]);
    }
  }, [subscriptionOptions, setValue]);

  useEffect(() => {
    if (waitListAvailable) {
      setValue("reservationType", "waitlist");
    } else {
      setValue("reservationType", "reserve");
    }
  }, [waitListAvailable, setValue]);

  const hasReachedCapacity =
    calendarItem?.spotsAvailable < 1 && calendarItem?.attendanceCap > 0;

  const hasDocumentsToSign = useMemo(
    () => documentsData?.getDocuments?.some((document) => !document.signed),
    [documentsData?.getDocuments],
  );

  const isFree = data?.getCalendarItem.calendarItemType?.isFree;

  const save = async (values: Form) => {
    if (!calendarItem?.uuid) return;

    try {
      addClientLoadingActions.on();

      const clientUserUuid = values.profile?.clientUserUuid;

      let userUuid: string | undefined = undefined;
      let subscriptionUuid: string | undefined = undefined;

      // If there isn't any available subscription, pass userUuid and subscriptionUuid as undefined
      if (
        !values.subscription ||
        !values.subscription?.isEligibleForBooking ||
        isFree
      ) {
        userUuid = values.profile?.userUuid;
      } else {
        subscriptionUuid = String(values.subscription?.subscriptionUuid);
      }

      if (!clientUserUuid) {
        throw new Error("Invalid client uuid");
      }

      switch (values.reservationType) {
        case "waitlist":
          await joinWaitlist({
            variables: {
              joinWaitlistInput: {
                clientUuid: client.uuid,
                clientUserUuid,
                calendarItemUuid: calendarItem.uuid,
                subscriptionUuid,
                userUuid,
                source,
                waitlistExpirationMins: getWaitlistExpirationOffset(
                  Number(values.waitlistExpireHours!.value),
                  Number(values.waitlistExpireMinutes!.value),
                ),
              },
            },
          });
          break;
        case "reserve":
          await createReservation({
            variables: {
              createReservationInput: {
                clientUserUuid,
                calendarItemUuid: calendarItem.uuid,
                subscriptionUuid,
                userUuid,
                overrideRules: true,
                clientUuid: client.uuid,
                source,
              },
            },
          });
          break;
        case "checkin":
          // eslint-disable-next-line no-case-declarations
          const createReservationCheckInResponse = await createReservation({
            variables: {
              createReservationInput: {
                clientUserUuid,
                calendarItemUuid: calendarItem.uuid,
                subscriptionUuid,
                userUuid,
                overrideRules: true,
                clientUuid: client.uuid,
                source,
              },
            },
          });
          if (createReservationCheckInResponse.data?.createReservation.uuid) {
            const registration =
              createReservationCheckInResponse.data?.createReservation;
            await checkInMutation.mutateAsync({
              registrationUuid: registration.uuid,
              shouldOverrideCheckinRules: true,
              source,
              isStaff: values.staffCheckIn,
              isCoaching: values.staffCheckIn && values.coachType === "coach",
              isAssisting:
                values.staffCheckIn && values.coachType === "assistant",
            });
          }
          break;
      }

      await sleep(1000);
      toast.success(
        `Client successfully added to ${calendarItem.title}`,
        defaultToastOptions,
      );
      onSuccess();
    } catch (err) {
      toast.error(
        (err as Error).message || "An error occurred",
        defaultToastOptions,
      );
    } finally {
      addClientLoadingActions.off();
    }
  };

  // reset the form if the profile is null
  useEffect(() => {
    if (!profile) {
      setValue("staffCheckIn", undefined);
      setValue("coachType", undefined);
      setValue("reservationType", undefined);
    }
  }, [profile, setValue]);

  // set the reservation type to checkin if staffCheckIn is true
  useEffect(() => {
    if (staffCheckIn) {
      setValue("reservationType", "checkin");
    }
  }, [staffCheckIn, setValue]);

  const shouldAllowSelectSubscription =
    profile?.userUuid && !isFree && calendarItem?.type === "Class";

  const isStaff = !!profile?.isStaff;

  const contentHeader = (
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      paddingX={3}
      paddingY={3}
    >
      <Grid item>
        <Text variant="heading-4">Add client</Text>
      </Grid>
      <Grid item>
        <Grid container>
          <Grid item>
            <Button
              text="Close"
              buttonType="secondary"
              size="medium"
              onClick={onClose}
            />
          </Grid>
          <Grid item>
            <Button
              text="Save"
              buttonType="primary"
              size="medium"
              type="button"
              disabled={!profile?.userUuid}
              loading={addClientLoading}
              onClick={handleSubmit(save)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const contentBody = (
    <>
      <MemberProfileSelect
        noPortal={noPortal}
        control={control}
        name="profile"
        label="Search for a member"
        required
        className="mt-4"
        placeholder="Search for a member or lead"
        onChange={() => setValue("subscription", undefined)}
        async
        hideDropdownIndicator
        isClearable
      />
      {isStaff && (
        <>
          <CheckboxController
            control={control}
            name="staffCheckIn"
            label="Staff check-in"
          />
          <Text variant="body-md" color="wolf-600" className="mt-1 mb-3">
            Select this option to create a coach or assistant coach check-in for
            payroll purposes.
          </Text>
          {staffCheckIn && (
            <RadioController
              options={[
                {
                  label: "Coach",
                  value: "coach",
                },
                {
                  label: "Assistant coach",
                  value: "assistant",
                },
              ]}
              control={control}
              name="coachType"
              label="Coach type"
              hideLabel
              required
            />
          )}
        </>
      )}
      {!staffCheckIn && (
        <>
          {shouldAllowSelectSubscription && (
            <SubscriptionSelect
              noPortal={noPortal}
              className={
                subscriptionOptions === undefined ||
                (subscriptionOptions.length === 0 &&
                  styles.hideSubscriptionSelect)
                  ? styles.hideSubscriptionSelect
                  : ""
              }
              calendarItemUuid={calendarUuid}
              control={control}
              name="subscription"
              label="Membership"
              menuPortalTarget={document.body}
              userUuid={profile?.userUuid}
              onLoadOptions={setSubscriptionOptions}
            />
          )}
          {subscriptionOptions !== undefined &&
            subscriptionOptions.length === 0 &&
            profile && (
              <div className="mb-4">
                <Toast
                  icon={Icons.Warning}
                  message="No active memberships"
                  iconColor="warning"
                />
              </div>
            )}
          {hasDocumentsToSign && profile && (
            <div className={`${styles.documentsWarning} mb-4`}>
              <Toast
                icon={Icons.Warning}
                message={
                  <>
                    Unsigned documents
                    <Button
                      className="ml-7"
                      buttonType="text"
                      href={`/open/documents/${profile.userUuid}`}
                      text="Sign now"
                      target="_blank"
                    />
                  </>
                }
                iconColor="warning"
              />
            </div>
          )}
          {subscription?.warningMessage && profile && (
            <div className="mb-4">
              <Toast
                icon={Icons.Warning}
                title={subscription.warningMessage.title || ""}
                message={subscription.warningMessage.content}
                iconColor="warning"
              />
            </div>
          )}
          {hasReachedCapacity && calendarItem && (
            <div className="mb-4">
              <Toast
                icon={Icons.Warning}
                title={`${calendarItem.type} full`}
                message={
                  reservationType === "waitlist"
                    ? ""
                    : `Adding this client will increase the ${calendarItem.type.toLowerCase()} capacity by (1).`
                }
                iconColor="warning"
              />
            </div>
          )}
          {profile && (
            <div data-cy="reservation-type">
              <RadioController
                options={[
                  {
                    label: "Waitlist",
                    value: "waitlist",
                    disabled: !waitListAvailable,
                  },
                  {
                    label: "Reserve",
                    value: "reserve",
                  },
                  {
                    label: "Check in",
                    value: "checkin",
                  },
                ]}
                direction="vertical"
                control={control}
                name="reservationType"
                label="Reservation type"
                hideLabel
                required
              />
            </div>
          )}
          {reservationType === "waitlist" && profile && (
            <>
              <Grid container alignItems="center" columnSpacing={0}>
                <Grid item xs={8}>
                  <Grid container alignItems="flex-end">
                    <Grid item xs={6}>
                      <ReactSelectController
                        control={control}
                        label="Waitlist expires"
                        name="waitlistExpireHours"
                        options={waitlistExpireHours}
                        className="mb-0"
                        menuPortalTarget={document.body}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReactSelectController
                        control={control}
                        label="Waitlist expires minutes"
                        name="waitlistExpireMinutes"
                        hideLabel
                        options={waitlistExpireMinutes}
                        className="mb-0"
                        menuPortalTarget={document.body}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Text variant="body-lg" className="mb-2">
                    before class start
                  </Text>
                </Grid>
              </Grid>
              <Text variant="body-md" color="wolf-600" className="mt-2 mb-2">
                If a spot opens up, how much advance notice does this client
                need to get to class on-time.
              </Text>
            </>
          )}
        </>
      )}
    </>
  );

  // This option used when triggered from another modal (e.g. ClassCheckInSheetContent)
  if (useDialog) {
    return (
      <DialogPortal>
        <DialogContent align="top" className="tw-max-w-sm">
          {contentHeader}
          <Divider />
          {contentBody}
        </DialogContent>
      </DialogPortal>
    );
  }

  return (
    <Modal.Card>
      {contentHeader}
      <Divider />
      <Modal.Content>{contentBody}</Modal.Content>
    </Modal.Card>
  );
};

export default AddMemberModal;
